// @systeme: interface
// La session de l'utilisatrice, partagée par tous les écrans : qui est connectée, dans
// quel espace. Le cookie fait l'authentification ; ici on garde ce que l'écran affiche.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Connexion, Inscription, ReponseSession } from '@foyer/shared';
import { api, quandSessionPerdue } from '../lib/api';

export type EtatSession =
  { etat: 'chargement' } | { etat: 'deconnectee' } | { etat: 'connectee'; session: ReponseSession };

interface ValeurContexte {
  etat: EtatSession;
  connecter(donnees: Connexion): Promise<void>;
  inscrire(donnees: Inscription): Promise<void>;
  deconnecter(): Promise<void>;
  mettreAJour(session: ReponseSession): void;
}

const Contexte = createContext<ValeurContexte | undefined>(undefined);

export function SessionProvider({
  children,
  initiale,
}: {
  children: ReactNode;
  initiale?: EtatSession;
}) {
  const [etat, setEtat] = useState<EtatSession>(initiale ?? { etat: 'chargement' });

  useEffect(() => {
    quandSessionPerdue(() => setEtat({ etat: 'deconnectee' }));
    if (initiale) return;
    api.auth
      .session()
      .then((session) => setEtat({ etat: 'connectee', session }))
      .catch(() => setEtat({ etat: 'deconnectee' }));
  }, [initiale]);

  const connecter = useCallback(async (donnees: Connexion) => {
    const session = await api.auth.connexion(donnees);
    setEtat({ etat: 'connectee', session });
  }, []);
  const inscrire = useCallback(async (donnees: Inscription) => {
    const session = await api.auth.inscription(donnees);
    setEtat({ etat: 'connectee', session });
  }, []);
  const deconnecter = useCallback(async () => {
    await api.auth.deconnexion();
    setEtat({ etat: 'deconnectee' });
  }, []);
  const mettreAJour = useCallback(
    (session: ReponseSession) => setEtat({ etat: 'connectee', session }),
    [],
  );

  const valeur = useMemo(
    () => ({ etat, connecter, inscrire, deconnecter, mettreAJour }),
    [etat, connecter, inscrire, deconnecter, mettreAJour],
  );
  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useSession(): ValeurContexte {
  const valeur = useContext(Contexte);
  if (!valeur) throw new Error('useSession doit être utilisé sous SessionProvider.');
  return valeur;
}

/** La session, garantie connectée (à utiliser dans les écrans protégés). */
export function useSessionConnectee(): ReponseSession {
  const { etat } = useSession();
  if (etat.etat !== 'connectee') throw new Error('Écran protégé rendu sans session.');
  return etat.session;
}
